"use client";

import React from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Plus, Ticket } from "lucide-react";
import { Button } from "../../button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../dropdown-menu";
import { useGlobalModalStack } from "@wtc/ui";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { FeatureFlags } from "@/lib/contexts/feature-flags-context";
import {
  CREATE_ACTIONS,
  SUPPORT_TICKET_ACTION,
  type CreateAction,
  type CreateActionGateContext,
} from "./create-actions";

interface CreateActionsMenuProps {
  canAccessCRM: boolean;
  isFeatureEnabled: (flagName: keyof FeatureFlags) => boolean;
  isWTCEmployee: boolean;
  /**
   * When true, the menu is restricted to the single client-allowed action:
   * creating a support ticket. Used for `client_user`s, whose only quick-create
   * capability is filing a ticket (CLIENT_USER_ACCESS_SCOPE). All CRM items are
   * suppressed regardless of `canAccessCRM`.
   */
  clientRestricted?: boolean;
}

function CreateActionItem({
  action,
  openModal,
  router,
}: {
  action: CreateAction;
  openModal: (modalId: string) => void;
  router: ReturnType<typeof useRouter>;
}) {
  const Icon = action.icon;
  const content = (
    <>
      <Icon className={action.iconClassName} />
      <div>
        <div className="font-medium">{action.label}</div>
        <div className="text-xs text-muted-foreground">{action.desc}</div>
      </div>
    </>
  );

  const act = action.action;
  if (act.type === "link") {
    return (
      <DropdownMenuItem asChild>
        <Link href={act.href} className="flex items-center cursor-pointer">
          {content}
        </Link>
      </DropdownMenuItem>
    );
  }

  const onClick =
    act.type === "modal" ? () => openModal(act.modalId) : () => router.push(act.href);

  return (
    <DropdownMenuItem className="flex items-center cursor-pointer" onClick={onClick}>
      {content}
    </DropdownMenuItem>
  );
}

export function CreateActionsMenu({
  canAccessCRM,
  isFeatureEnabled,
  isWTCEmployee,
  clientRestricted = false,
}: CreateActionsMenuProps) {
  const router = useRouter();
  const { openModal } = useGlobalModalStack();
  const contractCreationEnabled = false; // Feature flag: enable when ready

  // A client can only create a ticket — and only when ticket creation is
  // permitted for them. A read-only client (canCreateTickets === false) gets a
  // disabled item with an explanatory tooltip rather than a dead button
  // (ERROR_STATES_ACTIONABLE).
  const canCreateTickets = isFeatureEnabled("canCreateTickets");

  if (clientRestricted) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label="Create"
            className="h-8 px-3 hover:bg-accent hover:shadow-sm transition-all min-h-[44px] md:min-h-[48px] lg:min-h-[36px] md:px-4 lg:px-3"
          >
            <Plus className="h-4 w-4 mr-2" />
            <span className="hidden xl:inline">Create</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>Create New</DropdownMenuLabel>
          <DropdownMenuSeparator />

          {canCreateTickets ? (
            <CreateActionItem
              action={SUPPORT_TICKET_ACTION}
              openModal={openModal}
              router={router}
            />
          ) : (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  {/* Disabled item: a span (not a dead button) so it can't fire
                      navigation; the tooltip explains why it's unavailable. */}
                  <div
                    aria-disabled="true"
                    className="flex items-center px-2 py-1.5 text-sm opacity-50 cursor-not-allowed select-none"
                  >
                    <Ticket className="mr-3 h-4 w-4 text-blue-500" />
                    <div>
                      <div className="font-medium">Support Ticket</div>
                      <div className="text-xs text-muted-foreground">
                        Ticket creation isn&apos;t available on your account
                      </div>
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="left">
                  Your account has read-only access. Contact WTC to file a support ticket.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  const gateCtx: CreateActionGateContext = {
    canAccessCRM,
    isFeatureEnabled,
    isWTCEmployee,
    contractCreationEnabled,
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label="Create"
          className="h-8 px-3 hover:bg-accent hover:shadow-sm transition-all min-h-[44px] md:min-h-[48px] lg:min-h-[36px] md:px-4 lg:px-3"
        >
          <Plus className="h-4 w-4 mr-2" />
          <span className="hidden xl:inline">Create</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Create New</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {CREATE_ACTIONS.filter((item) => item.gate(gateCtx)).map((item) => (
          <CreateActionItem
            key={item.key}
            action={item}
            openModal={openModal}
            router={router}
          />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
